/**
 * Avatar — circular member avatar used across sharing UI (ShareModal,
 * SharedWithYouModal, the gallery "Shared with you" card).
 *
 * Renders the avatar image when a uri is provided, otherwise falls back to the
 * first letter of the name on an accent-colored circle. Size-driven: the font
 * scales with the circle so the initial reads well at any size.
 */
import React from "react";
import { View, Text, Image, StyleSheet, StyleProp, ViewStyle } from "react-native";
import { colors, textStyle } from "../styles";

interface Props {
  /** Diameter of the circle in px. */
  size: number;
  /** Display name — its first letter is the fallback initial. */
  name?: string;
  /** Avatar image URI; when absent the initial fallback is shown. */
  uri?: string;
  /** Extra style on the outer circle (e.g. overlap margin). */
  style?: StyleProp<ViewStyle>;
}

export default function Avatar({ size, name, uri, style }: Props) {
  const circle = { width: size, height: size, borderRadius: size / 2 };
  return (
    <View style={[styles.circle, circle, style]}>
      {uri ? (
        <Image source={{ uri }} style={circle} resizeMode="cover" />
      ) : (
        <Text style={[styles.initial, { fontSize: Math.round(size * 0.4) }]}>
          {(name || "?").charAt(0).toUpperCase()}
        </Text>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  circle: {
    backgroundColor: colors.accent,
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  initial: {
    ...textStyle.heading,
    color: colors.white,
  },
});
